import { AuthenticatedUser } from '@gettactic/api';
import React, { useState } from 'react';
import semver from 'semver';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import {
  MapCurrentPosition,
  MapInteractiveZoneActionOptions,
  MapInteractiveZones,
  Shape,
  SpotShape,
  SpotShape001
} from '../../../../types';
import { MAP_BUILDER_VERSION } from '../../../../version';
import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';
import { SpotLabelPatternItem, StagePosition } from '../../types';
import { MapCanvasState, MapCanvasValue } from './MapCanvasContext.types';
import { MapCanvasContext } from './useMapCanvas';

export interface MapCanvasProviderProps {
  value?: MapCanvasValue;
  isViewMode?: boolean;
  initialPosition?: MapCurrentPosition;
  interactiveZones?: MapInteractiveZones;
  highlightResource?: (
    resourceId: string | null,
    options: MapInteractiveZoneActionOptions
  ) => void;
  isPrivacyMode?: boolean;
  authenticatedUser?: AuthenticatedUser;
}

const minScale = 0.1;
const maxScale = 3;

export const MapCanvasProvider: React.FC<
  React.PropsWithChildren<MapCanvasProviderProps>
> = ({
  children,
  value,
  initialPosition,
  interactiveZones,
  highlightResource,
  isViewMode = false,
  isPrivacyMode = false,
  authenticatedUser
}) => {
  const version = MAP_BUILDER_VERSION;
  const { width, height } = useWindowSize();
  const [stagePosition, setStagePosition] = React.useState<StagePosition>({
    x: initialPosition ? initialPosition.x || 0 : 0,
    y: initialPosition ? initialPosition.y || 0 : 0,
    scale: initialPosition ? initialPosition.scaleX || 1 : 1
  });
  const [lastSpotLabel, setLastSpotLabel] = useState<string[] | null>([
    'A',
    '0'
  ]);
  const [spotLabelPattern, setSpotLabelPattern] = useState<
    SpotLabelPatternItem[]
  >([
    { type: 'text', value: '' },
    { type: 'numbers', depth: 9999 }
  ]);
  const [isDark] = useState(false);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [gridSize, setGridSize] = React.useState(20);
  const [editingZoneWidth, setEditingZoneWidth] = React.useState(2200);
  const [editingZoneHeight, setEditingZoneHeight] = React.useState(1800);
  const [minSelectionDistance] = React.useState(5);
  const [hoveringNode, setHoveringNode] = React.useState<
    SpotShape | null | undefined
  >(null);
  const [selectionBox, setSelectionBox] = React.useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [selectionStart, setSelectionStart] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [shapesInSelection, setShapesInSelection] = React.useState<string[]>(
    []
  );
  const [isAltPressed, setIsAltPressed] = React.useState(false);
  const [dragStartPositions, setDragStartPositions] = React.useState<{
    [key: string]: { x: number; y: number };
  }>({});

  React.useEffect(() => {
    window.document.body.classList.remove('dark');
    if (isDark) window.document.body.classList.add('dark');
  }, [isDark]);

  const valueData = JSON.stringify(value);

  React.useEffect(() => {
    if (value) {
      // Preventing the data to be erased when the value changes? TODO - Check this
      //if (value && shapes.length === 0) {

      setShapes(
        value.shapes.map((shape) => {
          if (!value.version) {
            if (shape.type === 'spot') {
              const oldShape = shape as unknown as SpotShape001;
              return {
                ...oldShape,
                ...oldShape.rows[0].items[0],
                rows: undefined,
                label: oldShape.rows[0].items[0].textProps?.text || ''
              } as SpotShape;
            }
          }

          return shape;
        })
      );
      setGridSize(value.gridSize);
      updateEditingZoneSize(value.editingZoneWidth, value.editingZoneHeight);
      if (
        value.lastSpotLabel &&
        value.version &&
        semver.gt(value.version, version)
      )
        setLastSpotLabel(value.lastSpotLabel);
      if (
        value.spotLabelPattern &&
        value.version &&
        semver.gt(value.version, version)
      )
        setSpotLabelPattern(value.spotLabelPattern);
    }
  }, [valueData]);

  const updateStagePosition = React.useCallback(
    (position: Partial<StagePosition>) => {
      setStagePosition((prev) => ({ ...prev, ...position }));
    },
    []
  );

  const updateEditingZoneSize = React.useCallback(
    (width: number, height: number) => {
      setEditingZoneWidth(width);
      setEditingZoneHeight(height);
    },
    []
  );

  const centerStage = React.useCallback(() => {
    const stage = getWindowKonvaObject().stages[0];
    if (!stage) return;

    const scale = Math.min(
      width / editingZoneWidth,
      height / editingZoneHeight
    );
    stage.scale({ x: scale, y: scale });

    // Calculate the center of the editing zone in stage coordinates.
    // Here we assume that stagePosition is the top-left of the editing zone.
    const editingZoneCenter = {
      x: editingZoneWidth / 2,
      y: editingZoneHeight / 2
    };

    // Calculate the new stage position so that the editing zone’s center is at canvas center.
    const newStagePos = {
      x: width / 2 - editingZoneCenter.x * scale,
      y: height / 2 - editingZoneCenter.y * scale
    };

    setStagePosition({
      x: newStagePos.x,
      y: newStagePos.y,
      scale: scale
    });

    // Update the stage position and redraw.
    stage.position(newStagePos);
    stage.batchDraw();
  }, [width, height, editingZoneWidth, editingZoneHeight, stagePosition]);

  const getCanvasValue = (): MapCanvasValue => {
    return {
      version,
      lastUpdate: new Date().toISOString(),
      shapes,
      gridSize,
      editingZoneWidth,
      editingZoneHeight,
      spotLabelPattern,
      lastSpotLabel
    };
  };

  const contextValues: MapCanvasState = {
    version,
    shapes,
    gridSize,
    editingZoneWidth,
    editingZoneHeight,
    minSelectionDistance,
    selectionBox,
    selectionStart,
    isDragging,
    shapesInSelection,
    isAltPressed,
    dragStartPositions,
    setGridSize,
    setSelectionBox,
    setSelectionStart,
    setIsDragging,
    setShapesInSelection,
    setIsAltPressed,
    setDragStartPositions,
    setShapes,
    isDark,
    lastSpotLabel,
    setLastSpotLabel,
    spotLabelPattern,
    setSpotLabelPattern,
    getCanvasValue,
    width,
    height,
    isViewMode,
    stagePosition,
    setStagePosition,
    updateStagePosition,
    updateEditingZoneSize,
    centerStage,
    minScale,
    maxScale,
    interactiveZones,
    highlightResource,
    isPrivacyMode,
    authenticatedUser,
    hoveringNode,
    setHoveringNode
  };

  return (
    <MapCanvasContext.Provider value={contextValues}>
      {children}
    </MapCanvasContext.Provider>
  );
};
